import {
  buildResizableImageUrl,
  capitalizeFirstLetter,
  capitalizeWords,
} from '@autone/utils';
import { useMemo, useState } from 'react';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Card,
  CardContent,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../atoms';
import { ArrowRightShortIcon } from '../../../icons';
import { SidebarSubItem } from '../components/SidebarSubItem';
import { type SidebarNavigationItemProps } from '../types/Sidebar.types';

interface SidebarUserCardProps {
  name?: string;
  surname?: string;
  username?: string;
  imageUrl?: string;
  email?: string;
  userOptionsConfig: SidebarNavigationItemProps[];
}

const getInitials = (name: string | undefined) => {
  if (!name) return '';
  return name
    .split(' ')
    .map((part) => part[0])
    .join('');
};

export const SidebarUserCard = ({
  name,
  surname,
  username,
  imageUrl,
  email,
  userOptionsConfig,
}: SidebarUserCardProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const userName = useMemo(
    () =>
      name
        ? `${capitalizeFirstLetter(name)} ${capitalizeFirstLetter(surname)}`
        : capitalizeWords(username),
    [name, surname, username],
  );

  const initials = useMemo(() => getInitials(userName), [userName]);

  const closePopover = () => setIsOpen(false);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <Card className="shadow-none cursor-pointer border-0 overflow-hidden">
        <PopoverTrigger asChild>
          <CardContent className="flex items-center p-0 gap-2">
            <Avatar className="rounded-full text-foreground bg-accent font-bold size-10">
              <AvatarImage
                src={imageUrl ? buildResizableImageUrl(imageUrl) : undefined}
              />
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <div className="group-data-[state=collapsed]:hidden flex w-full">
              <div className="flex flex-col">
                <div className="text-base font-medium text-foreground truncate leading-tight">
                  {userName}
                </div>
                <div className="text-xxs text-muted-foreground truncate">
                  {email}
                </div>
              </div>
              <div className="ml-auto self-center pr-4">
                <ArrowRightShortIcon className="text-white" size="sm" />
              </div>
            </div>
          </CardContent>
        </PopoverTrigger>
        <PopoverContent className="dark p-2" side="right" align="end">
          <ul className="space-y-0.5">
            {userOptionsConfig.map((item) => {
              return (
                <SidebarSubItem
                  key={item.title}
                  subItem={{
                    ...item,
                    // when an on click is passed the internal component ignores the href routing, so this needs to be passed conditionally
                    ...(item.onClick && {
                      onClick: () => {
                        closePopover();
                        item.onClick?.();
                      },
                    }),
                  }}
                />
              );
            })}
          </ul>
        </PopoverContent>
      </Card>
    </Popover>
  );
};
